import React, { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { Trans, useTranslation } from "react-i18next";

import { Box, Button, Link, Spacing, Stack } from "@noom/wax-component-library";

import { InformationalScreen } from "@/components/survey/questionTypes/core";
import { ProgramEligibilityFooter } from "@/components/survey/questionTypes/dpp-triage";
import { RetriageFinishedStatus } from "@/constants";
import { useAppContext } from "@/contexts";
import { useGoto, useRecordRetriageOutcome, useTrackEvent } from "@/hooks";

const QUESTION_ID = "retriageFinished";

export const RetriageFinished: React.FC = () => {
  const { retriageEligibility: { accessCode } = {}, retriageFinishedStatus } =
    useAppContext();
  const { notFound, openApp } = useGoto();
  const { recordRetriageOutcome, waitForRetriageOutcome } =
    useRecordRetriageOutcome();
  const [isLeaving, setIsLeaving] = useState(false);
  const { trackAnonEvent, trackSensitiveEvent } = useTrackEvent();
  const { t } = useTranslation();

  useEffect(() => {
    if (
      !retriageFinishedStatus &&
      window.ENV.NOOM_ENVIRONMENT !== "development"
    ) {
      notFound();
      return;
    }

    if (retriageFinishedStatus) {
      trackAnonEvent("b2bEnrollmentAnonUserExcluded", {
        exclusionReason: retriageFinishedStatus,
      });

      trackSensitiveEvent("b2bEnrollmentSensitiveUserExcluded", {
        exclusionReason: retriageFinishedStatus,
      });
    }

    if (retriageFinishedStatus && accessCode) {
      recordRetriageOutcome(
        accessCode,
        retriageFinishedStatus === RetriageFinishedStatus.RECENTLY_RETRIAGED
          ? "RECENTLY_RETRIAGED"
          : "KEPT_SAME_OFFERING",
      );
    }
  }, [retriageFinishedStatus]);

  const onClickReturnToApp = async () => {
    setIsLeaving(true);
    await waitForRetriageOutcome();
    openApp();
    setIsLeaving(false);
  };

  const supportLink = (
    <Link colorScheme="info" href="mailto:partnersupport@noom.com">
      partnersupport@noom.com
    </Link>
  );

  return (
    <InformationalScreen
      heading={
        <Trans
          i18nKey="retriageFinished.title"
          context={retriageFinishedStatus || "ALREADY_MED"}
        />
      }
      questionId={QUESTION_ID}
    >
      <Stack gap={Spacing[5]}>
        <Box>
          <Trans
            i18nKey="retriageFinished.message"
            context={retriageFinishedStatus || "ALREADY_MED"}
            components={{
              supportLink,
            }}
          />
        </Box>
      </Stack>
      {isMobile && (
        <Box
          alignItems="center"
          display="flex"
          flexDirection="column"
          marginTop="var(--spacing-xl)"
        >
          <Button
            colorScheme="primary"
            isLoading={isLeaving}
            onClick={onClickReturnToApp}
            padding="unset"
            size="xl"
            whiteSpace="unset"
          >
            {t("common.returnToApp")}
          </Button>
        </Box>
      )}
      {retriageFinishedStatus ===
        RetriageFinishedStatus.EXTERNAL_GLP1_NO_ELIGIBLE_PROGRAMS && (
        <Box marginTop="var(--spacing-xl)">
          <ProgramEligibilityFooter />
        </Box>
      )}
    </InformationalScreen>
  );
};
